import { InfoOutlined } from '@mui/icons-material';
import { Box, Card, CardContent, Tooltip, Typography, useTheme, Skeleton } from '@mui/material';
import { type FC, useMemo, useState } from 'react';

import { getUserFacingCategoryName } from '@core/constants/product';
import type { ActualsPlantPerformanceSummary } from '@core/types/api/ActualsPlantPerformanceSummary/ActualsPlantPerformanceSummary';
import Enums from '@core/types/api/Enums';
import {
  getActualsQuantityShortUnitDisplay,
  getActualsQuantityUnitDisplay,
} from '@core/utils/actualsQuantityUnitDisplay';
import { hasValue } from '@core/utils/equality';
import { formatNumber } from '@core/utils/format';
import { ParseNullableNumber } from '@core/utils/formParsingHelpers';
import { categoryTabs } from '@core/utils/productCategoryFilter';
import { type ExportColumn, toNumberOrNull } from '@core/utils/tableExport';
import { getUnitLabel } from '@core/utils/unitHelpers';
import { TypographyWithTooltip } from '@web/components';
import type { LocationPerformanceData } from '@web/utils/backlog';

import { CardTabs } from '../components/CardTabs';
import { BudgetUtilizationBar } from './BudgetUtilizationBar';

const METRIC_LABELS = {
  location: 'Location',
  forecast: 'Forecast',
  variance: 'Variance',
  variancePercent: 'Variance %',
  budget: 'Budget',
  capacity: 'Capacity',
  budgetUtilizationPercent: 'Budget Utilization %',
} as const;

const CATEGORIES = [
  Enums.ProductCategory.ReadyMix,
  Enums.ProductCategory.Asphalt,
  Enums.ProductCategory.Aggregate,
];

// Budget and capacity are stored per location, not per product type (sc-11714):
// their forecast rows carry no product_category and the schema permits only one
// per location per interval. They are rendered outside the category tabs and in
// their own unit, so every tab shows the same figure. Wording matches the
// Forecasts page so the two screens explain it identically.
const perLocationTooltip = (label: string): string =>
  `${label} is tracked per location and is not filtered by product type.`;

const PerLocationInfo: FC<{ label: string }> = ({ label }) => (
  <Tooltip title={perLocationTooltip(label)}>
    <InfoOutlined
      sx={{
        position: 'relative',
        top: 2,
        fontSize: '0.875rem',
        ml: 0.5,
      }}
    />
  </Tooltip>
);

const mixedUnitExportColumns = (
  label: string,
  getRawValue: (
    row: ActualsPlantPerformanceSummary,
    category: Enums.ProductCategory,
  ) => string | null,
): ExportColumn<ActualsPlantPerformanceSummary>[] =>
  CATEGORIES.map((category) => ({
    header: `${label} ${getUserFacingCategoryName(category)} (${getActualsQuantityShortUnitDisplay(category)})`,
    getValue: (row: ActualsPlantPerformanceSummary): number | null =>
      toNumberOrNull(getRawValue(row, category)),
  }));

const mixedPercentExportColumns = (
  label: string,
  getRawValue: (
    row: ActualsPlantPerformanceSummary,
    category: Enums.ProductCategory,
  ) => string | null,
): ExportColumn<ActualsPlantPerformanceSummary>[] =>
  CATEGORIES.map((category) => ({
    header: `${label} ${getUserFacingCategoryName(category)} (%)`,
    getValue: (row: ActualsPlantPerformanceSummary): number | null =>
      toNumberOrNull(getRawValue(row, category)),
  }));

export const locationPerformanceExportColumns =
  (): ExportColumn<ActualsPlantPerformanceSummary>[] => [
    { header: METRIC_LABELS.location, getValue: (row) => row.name },
    ...mixedUnitExportColumns(METRIC_LABELS.forecast, (row, category) => {
      if (category === Enums.ProductCategory.ReadyMix) return row.plannedVolumeMix;
      if (category === Enums.ProductCategory.Asphalt) return row.plannedVolumeAsphalt;
      return row.plannedVolumeAggregate;
    }),
    ...mixedUnitExportColumns(METRIC_LABELS.variance, (row, category) => {
      if (category === Enums.ProductCategory.ReadyMix) return row.varianceMix;
      if (category === Enums.ProductCategory.Asphalt) return row.varianceAsphalt;
      return row.varianceAggregate;
    }),
    ...mixedPercentExportColumns(METRIC_LABELS.variancePercent, (row, category) => {
      if (category === Enums.ProductCategory.ReadyMix) return row.plannedActualPercentageMix;
      if (category === Enums.ProductCategory.Asphalt) return row.plannedActualPercentageAsphalt;
      return row.plannedActualPercentageAggregate;
    }),
    {
      header: `${METRIC_LABELS.budget} (per location)`,
      getValue: (row: ActualsPlantPerformanceSummary): number | null => toNumberOrNull(row.budget),
    },
    {
      header: `${METRIC_LABELS.budget} Unit`,
      getValue: (row: ActualsPlantPerformanceSummary): string | null =>
        hasValue(row.budget) ? getUnitLabel(row.budgetUnit) : null,
    },
    {
      header: `${METRIC_LABELS.capacity} (per location)`,
      getValue: (row: ActualsPlantPerformanceSummary): number | null =>
        toNumberOrNull(row.capacity),
    },
    {
      header: `${METRIC_LABELS.capacity} Unit`,
      getValue: (row: ActualsPlantPerformanceSummary): string | null =>
        hasValue(row.capacity) ? getUnitLabel(row.capacityUnit) : null,
    },
    ...mixedPercentExportColumns(METRIC_LABELS.budgetUtilizationPercent, (row, category) => {
      if (category === Enums.ProductCategory.ReadyMix) return row.budgetUtilizationPercentageMix;
      if (category === Enums.ProductCategory.Asphalt) return row.budgetUtilizationPercentageAsphalt;
      return row.budgetUtilizationPercentageAggregate;
    }),
  ];

const getLocationPerformanceData = (
  data: ActualsPlantPerformanceSummary,
): LocationPerformanceData[] => {
  return [
    {
      category: Enums.ProductCategory.ReadyMix,
      plannedVolume: data.plannedVolumeMix,
      variance: data.varianceMix,
      plannedActualPercentage: data.plannedActualPercentageMix,
      budgetUtilizationPercentage: data.budgetUtilizationPercentageMix,
      unit: getActualsQuantityUnitDisplay(Enums.ProductCategory.ReadyMix),
    },
    {
      category: Enums.ProductCategory.Asphalt,
      plannedVolume: data.plannedVolumeAsphalt,
      variance: data.varianceAsphalt,
      plannedActualPercentage: data.plannedActualPercentageAsphalt,
      budgetUtilizationPercentage: data.budgetUtilizationPercentageAsphalt,
      unit: getActualsQuantityUnitDisplay(Enums.ProductCategory.Asphalt),
    },
    {
      category: Enums.ProductCategory.Aggregate,
      plannedVolume: data.plannedVolumeAggregate,
      variance: data.varianceAggregate,
      plannedActualPercentage: data.plannedActualPercentageAggregate,
      budgetUtilizationPercentage: data.budgetUtilizationPercentageAggregate,
      unit: getActualsQuantityUnitDisplay(Enums.ProductCategory.Aggregate),
    },
  ];
};

type Props = {
  data: ActualsPlantPerformanceSummary;
};

export const LocationPerformanceCard: FC<Props> = ({ data }) => {
  const theme = useTheme();

  const allPerformanceData = useMemo(() => getLocationPerformanceData(data), [data]);

  // The backend decides tab presence and returns it, so the tabs and the
  // productCategory filter cannot drift apart (sc-12831).
  const filteredTabsByData = useMemo(
    () => categoryTabs.filter((tab) => data.productCategory.includes(tab.value)),
    [categoryTabs, data],
  );

  const [activeTab, setActiveTab] = useState<Enums.ProductCategory>(
    filteredTabsByData[0]?.value ?? Enums.ProductCategory.ReadyMix,
  );

  const locationPerformanceData = allPerformanceData.find((item) => item.category === activeTab);

  return (
    <Card
      sx={{
        width: '100%',
        maxWidth: 320,
        border: `1.5px solid ${theme.palette.SlabGray.Border}`,
        borderRadius: '18px',
        boxShadow: `0 2px 12px 0 ${theme.palette.SlabGray[800]}15`,
        display: 'flex',
        alignItems: 'center',
        transition: 'box-shadow 0.2s',
        '&:hover': {
          boxShadow: `0 4px 18px 0 ${theme.palette.SlabGray[800]}20`,
        },
        p: 0,
      }}
    >
      <CardContent
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: 1,
        }}
      >
        <Box
          display='flex'
          flexDirection='column'
          height='100%'
          justifyContent='space-between'
          alignItems='flex-start'
        >
          <Box sx={{ width: '100%' }}>
            <TypographyWithTooltip
              variant='h5'
              mb={1.5}
              text={data.name}
              sx={{
                lineHeight: 1.2,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',

                maxHeight: '2.7em',
              }}
            >
              {data.name}
            </TypographyWithTooltip>

            <CardTabs tabs={filteredTabsByData} activeTab={activeTab} setActiveTab={setActiveTab} />

            {hasValue(locationPerformanceData) && (
              <Typography
                fontWeight={500}
                variant='body2'
                mb={1}
                color={theme.palette.SlabGray[500]}
              >
                {METRIC_LABELS.forecast}:{' '}
                <Box component='span' color={theme.palette.SlabGray[600]} fontWeight={500}>
                  {locationPerformanceData.plannedVolume !== null
                    ? `${formatNumber(locationPerformanceData.plannedVolume)} ${locationPerformanceData.unit}`
                    : '-'}
                </Box>
              </Typography>
            )}

            {hasValue(locationPerformanceData) && (
              <Typography
                fontWeight={500}
                variant='body2'
                mb={1}
                color={theme.palette.SlabGray[500]}
              >
                {METRIC_LABELS.variance}:{' '}
                <Box component='span' color={theme.palette.SlabGray[600]} fontWeight={500}>
                  {locationPerformanceData.variance !== null
                    ? `${formatNumber(locationPerformanceData.variance)} ${locationPerformanceData.plannedActualPercentage !== null ? `(${locationPerformanceData.plannedActualPercentage}%)` : ''}`
                    : '-'}
                </Box>
              </Typography>
            )}

            {hasValue(locationPerformanceData) && (
              <Typography
                fontWeight={500}
                variant='body2'
                mb={1}
                color={theme.palette.SlabGray[500]}
              >
                {METRIC_LABELS.budget}
                <PerLocationInfo label={METRIC_LABELS.budget} />:{' '}
                <Box component='span' color={theme.palette.SlabGray[600]} fontWeight={500}>
                  {hasValue(data.budget)
                    ? `${formatNumber(data.budget)} ${getUnitLabel(data.budgetUnit)}`
                    : '-'}
                </Box>
              </Typography>
            )}

            {hasValue(locationPerformanceData) && (
              <Typography
                fontWeight={500}
                variant='body2'
                mb={1}
                color={theme.palette.SlabGray[500]}
              >
                {METRIC_LABELS.capacity}
                <PerLocationInfo label={METRIC_LABELS.capacity} />:{' '}
                <Box component='span' color={theme.palette.SlabGray[600]} fontWeight={500}>
                  {hasValue(data.capacity)
                    ? `${formatNumber(data.capacity)} ${getUnitLabel(data.capacityUnit)}`
                    : '-'}
                </Box>
              </Typography>
            )}
          </Box>

          <Box mt={1.5} width='100%'>
            {hasValue(locationPerformanceData) && (
              <BudgetUtilizationBar
                plantId={data.id}
                budgetPercentage={
                  locationPerformanceData.budgetUtilizationPercentage === null
                    ? null
                    : ParseNullableNumber(locationPerformanceData.budgetUtilizationPercentage)
                }
                budget={data.budget}
              />
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export const LocationPerformanceCardSkeleton: FC = () => {
  const theme = useTheme();

  return (
    <Card
      sx={{
        width: '100%',
        maxWidth: 320,
        border: `1.5px solid ${theme.palette.SlabGray.Border}`,
        borderRadius: '18px',
        boxShadow: `0 2px 12px 0 ${theme.palette.SlabGray[800]}15`,
        p: 0,
      }}
    >
      <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Skeleton variant='text' width='60%' height={24} />

        <Box display='flex' gap={1} mb={0.5}>
          {categoryTabs.map((tab) => (
            <Skeleton key={tab.value} variant='rounded' width={64} height={24} />
          ))}
        </Box>

        {[
          METRIC_LABELS.forecast,
          METRIC_LABELS.variance,
          METRIC_LABELS.budget,
          METRIC_LABELS.capacity,
        ].map((label) => (
          <Skeleton key={label} variant='text' width='60%' height={20} />
        ))}
      </CardContent>
    </Card>
  );
};
